import { PluginConfig } from '../../types/PluginInterface';

// Catégories dédiées location de vacances
export type VacationCategory = 'villa' | 'maison' | 'appartement' | 'gite' | 'chalet' | 'studio' | 'glamping' | 'camping';
// Legacy (backward compat)
export type RentalCategory = VacationCategory | 'real_estate' | 'vacation' | 'vehicle' | 'equipment';

export const CATEGORY_LABELS: Record<string, string> = {
  villa: 'Villa',
  maison: 'Maison',
  appartement: 'Appartement',
  gite: 'Gîte',
  chalet: 'Chalet',
  studio: 'Studio',
  glamping: 'Glamping',
  camping: 'Camping',
  real_estate: 'Immobilier',
  vacation: 'Vacances',
};

export const CATEGORY_ICONS: Record<string, string> = {
  villa: '🏖️',
  maison: '🏡',
  appartement: '🏢',
  gite: '🌾',
  chalet: '🏔️',
  studio: '🏠',
  glamping: '⛺',
  camping: '🏕️',
  real_estate: '🏠',
  vacation: '🏖️',
};

export interface AmenityDef {
  id: string;
  label: string;
  icon: string;
}

export const VACATION_AMENITIES: AmenityDef[] = [
  { id: 'wifi', label: 'WiFi', icon: '📶' },
  { id: 'pool', label: 'Piscine', icon: '🏊' },
  { id: 'parking', label: 'Parking', icon: '🅿️' },
  { id: 'kitchen', label: 'Cuisine équipée', icon: '🍳' },
  { id: 'ac', label: 'Climatisation', icon: '❄️' },
  { id: 'heating', label: 'Chauffage', icon: '🔥' },
  { id: 'washer', label: 'Lave-linge', icon: '🧺' },
  { id: 'dryer', label: 'Sèche-linge', icon: '💨' },
  { id: 'dishwasher', label: 'Lave-vaisselle', icon: '🍽️' },
  { id: 'tv', label: 'TV / Netflix', icon: '📺' },
  { id: 'bbq', label: 'Barbecue', icon: '🍖' },
  { id: 'terrace', label: 'Terrasse', icon: '🪑' },
  { id: 'garden', label: 'Jardin', icon: '🌿' },
  { id: 'beach', label: 'Accès plage', icon: '🏖️' },
  { id: 'sea-view', label: 'Vue mer', icon: '🌊' },
  { id: 'mountain-view', label: 'Vue montagne', icon: '⛰️' },
  { id: 'jacuzzi', label: 'Jacuzzi', icon: '🛁' },
  { id: 'gym', label: 'Salle de sport', icon: '💪' },
  { id: 'pets', label: 'Animaux acceptés', icon: '🐾' },
  { id: 'baby', label: 'Équipements bébé', icon: '👶' },
  { id: 'workspace', label: 'Espace travail', icon: '💻' },
  { id: 'ev-charger', label: 'Borne recharge VE', icon: '⚡' },
  { id: 'boat', label: 'Accès bateau', icon: '⛵' },
  { id: 'fireplace', label: 'Cheminée', icon: '🪵' },
];

export interface VacationCapacity {
  guests: number;
  bedrooms: number;
  beds: number;
  bathrooms: number;
}

export interface VacationRules {
  checkInTime?: string;    // ex: "15:00"
  checkOutTime?: string;   // ex: "11:00"
  minNights?: number;
  maxNights?: number;
  smokingAllowed?: boolean;
  petsAllowed?: boolean;
  eventsAllowed?: boolean;
}

export interface SeasonalPrice {
  id: string;
  label: string;       // ex: "Été 2025"
  startDate: string;   // YYYY-MM-DD
  endDate: string;     // YYYY-MM-DD
  pricePerDay: number;
}

export interface LongStayDiscount {
  minNights: number;       // ex: 7
  discountPercent: number; // ex: 10
}

export interface AvailabilityRange {
  startDate: string;
  endDate: string;
  type: 'booking' | 'blocked';
  reason?: string;
  bookingId?: string;
  bookingDetails?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    paymentMethod?: string;
    totalPrice?: number;
    currency?: string;
    transactionId?: string;
    createdAt?: string;
    pluginInstanceId?: string;
    itemName?: string;
    guestsCount?: number;
  };
}

export interface VacationReview {
  id: string;
  rentalItemId: string;
  guestName: string;
  stars: number;   // 1–5
  comment?: string;
  createdAt: string;
  ownerReply?: string;
}

export interface RentalNotificationSettings {
  // Notifications propriétaire
  ownerEmailOnBooking?: boolean;
  ownerEmailOnCancellation?: boolean;
  ownerNotifOnBooking?: boolean;
  // Notifications voyageur
  guestEmailOnBooking?: boolean;
  guestEmailOnCancellation?: boolean;
  // Templates email
  ownerBookingEmailSubject?: string;
  ownerBookingEmailBody?: string;
  guestBookingEmailSubject?: string;
  guestBookingEmailBody?: string;
}

export interface CustomBadge {
  id: string;
  label: string;
  bg: string;    // ex: '#e63946'
  color: string; // ex: '#ffffff'
}

export interface RentalItem {
  id: string;
  name: string;
  category: RentalCategory;
  description: string;
  badge?: string;

  // Médias
  images: string[];
  videoUrl?: string;

  // Capacité
  capacity?: VacationCapacity;

  // Équipements (tableau d'IDs VACATION_AMENITIES)
  amenities?: string[];

  // Règles du bien
  rules?: VacationRules;

  // Tarification
  pricePerDay: number;
  weekendPricePerDay?: number;       // vendredi + samedi
  cleaningFee?: number;
  seasonalPrices?: SeasonalPrice[];
  longStayDiscounts?: LongStayDiscount[];
  lastMinuteDiscountPercent?: number; // % si J-3

  // Géolocalisation
  address?: string;
  latitude?: number;
  longitude?: number;

  // iCal sync (import Airbnb, Booking.com...)
  icalImportUrl?: string;

  // Planning
  availability?: AvailabilityRange[];

  // Legacy (backward compat)
  features?: Record<string, string>;
}

export interface RentalConfig extends PluginConfig {
  id?: string;
  userId?: string;
  ownerId?: string | number;
  title: string;
  description?: string;
  items: RentalItem[];
  currency: string;

  // Mode réservation
  bookingMode?: 'instant' | 'request';

  // Paiement
  enablePayments: boolean;
  stripeKeyId?: number;
  paypalKeyId?: number;
  googleMapsKeyId?: number;

  // Caution / dépôt de garantie
  depositAmount?: number;
  depositMode?: 'preauth' | 'charge' | 'none';
  depositRefundDays?: number;

  // Taxe de séjour (par personne par nuit)
  touristTaxPerPersonPerNight?: number;

  // Avis voyageurs
  enableReviews?: boolean;

  // Équipements & badges personnalisés
  customAmenities?: AmenityDef[];
  customBadges?: CustomBadge[];

  // Notifications / Emails
  notificationSettings?: RentalNotificationSettings;

  // Affichage
  showSearchBar?: boolean;
  displayMode: 'list' | 'grid';
  primaryColor?: string;
  backgroundColor?: string;

  // Styles titre section
  titleFontFamily?: string;
  titleFontSize?: string;
  titleColor?: string;
  titleBold?: boolean;
  titleItalic?: boolean;
  titleUnderline?: boolean;

  // Styles description section
  descriptionFontFamily?: string;
  descriptionFontSize?: string;
  descriptionColor?: string;
  descriptionBold?: boolean;
  descriptionItalic?: boolean;
  descriptionUnderline?: boolean;

  // Styles annonce
  itemBackgroundColor?: string;
  itemTitleFontFamily?: string;
  itemTitleFontSize?: string;
  itemTitleColor?: string;
  itemTitleBold?: boolean;
  itemTitleItalic?: boolean;
  itemTitleUnderline?: boolean;
  itemDescriptionFontFamily?: string;
  itemDescriptionFontSize?: string;
  itemDescriptionColor?: string;
  itemDescriptionBold?: boolean;
  itemDescriptionItalic?: boolean;
  itemDescriptionUnderline?: boolean;
  itemPriceFontFamily?: string;
  itemPriceFontSize?: string;
  itemPriceColor?: string;
  itemPriceBold?: boolean;
  itemPriceItalic?: boolean;
  itemPriceUnderline?: boolean;
  itemButtonText?: string;
  itemButtonBackgroundColor?: string;
  itemButtonTextColor?: string;
  itemButtonFontFamily?: string;
  itemButtonFontSize?: string;
  itemButtonBold?: boolean;
  itemButtonItalic?: boolean;
  itemButtonUnderline?: boolean;

  // Pagination
  paginationBackgroundColor?: string;
  paginationActiveBackgroundColor?: string;
  paginationTextColor?: string;
  paginationActiveTextColor?: string;
  paginationFontFamily?: string;
  paginationFontSize?: string;
  paginationBold?: boolean;
  paginationItalic?: boolean;
  paginationUnderline?: boolean;
}
